import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Radio, Users, Trophy, Zap, Tv, Layers } from "lucide-react";

/**
 * /broadcast — Read-only TV cinema view for umpires + on-venue TV walls.
 * Query params:
 *   ?mode=single|all|standings|bracket|wildcard|sponsor|auto
 *   ?court=Court%201 (for mode=single)
 *   ?cycle=15 (seconds per mode in auto-cycle)
 *
 * All data via public endpoints — no login required.
 * Auto-refresh every 5s. Full-viewport black background, huge typography.
 */
const AUTO_CYCLE_MODES = ["all", "standings", "bracket", "wildcard", "sponsor"];

export default function Broadcast() {
  const [search, setSearch] = useSearchParams();
  const initialMode = search.get("mode") || "auto";
  const [mode, setMode] = useState(initialMode);
  const court = search.get("court") || "Court 1";
  const cycleSec = parseInt(search.get("cycle") || "15", 10);

  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [qf, setQF] = useState(null);
  const [wildcards, setWildcards] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [m, s, q, w] = await Promise.all([
          api.get("/public/matches").catch(() => ({ data: [] })),
          api.get("/public/standings").catch(() => ({ data: [] })),
          api.get("/public/qf/reveal").catch(() => ({ data: {} })),
          api.get("/public/qf/wildcard-report").catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setMatches(Array.isArray(m.data) ? m.data : m.data.items || []);
        setStandings(Array.isArray(s.data) ? s.data : s.data.items || []);
        setQF(q.data || {});
        setWildcards((w.data && w.data.candidates) || []);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(() => { load(); setTick((x) => x + 1); }, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Auto-cycle rotation.
  const [autoIdx, setAutoIdx] = useState(0);
  useEffect(() => {
    if (initialMode !== "auto") return;
    const t = setInterval(() => setAutoIdx((i) => (i + 1) % AUTO_CYCLE_MODES.length), cycleSec * 1000);
    return () => clearInterval(t);
  }, [initialMode, cycleSec]);
  const effectiveMode = initialMode === "auto" ? AUTO_CYCLE_MODES[autoIdx] : mode;

  const liveMatches = useMemo(() => matches.filter((m) => (m.status || "").toLowerCase() === "live"), [matches]);

  const changeMode = (m) => { setMode(m); const s = new URLSearchParams(search); s.set("mode", m); setSearch(s); };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden" data-testid="broadcast-page">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(90deg, transparent 39px, rgba(255,255,255,0.15) 40px), linear-gradient(transparent 39px, rgba(255,255,255,0.15) 40px)", backgroundSize: "40px 40px" }} />

      {/* Header bar */}
      <div className="relative z-10 flex items-center justify-between px-8 py-4 border-b border-white/10 bg-black/60">
        <div className="flex items-center gap-3">
          <Tv className="w-5 h-5 text-[#F4B400]" />
          <div className="font-anton text-2xl uppercase tracking-tight">Mara League · Broadcast</div>
          {initialMode === "auto" && (
            <div className="ml-3 px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full bg-[#F4B400]/20 border border-[#F4B400]/40 text-[#F4B400]" data-testid="broadcast-cycle-indicator">
              AUTO · {effectiveMode.toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1" data-testid="broadcast-mode-switcher">
          {[
            { k: "single", l: "Single", icon: Radio },
            { k: "all", l: "All Courts", icon: Layers },
            { k: "standings", l: "Standings", icon: Users },
            { k: "bracket", l: "Bracket", icon: Trophy },
            { k: "wildcard", l: "Wildcard", icon: Zap },
            { k: "sponsor", l: "Sponsor", icon: Tv },
            { k: "auto", l: "Auto", icon: Tv },
          ].map((m) => {
            const active = (initialMode === "auto" && m.k === "auto") || (initialMode !== "auto" && effectiveMode === m.k);
            const I = m.icon;
            return (
              <button
                key={m.k}
                onClick={() => changeMode(m.k)}
                data-testid={`broadcast-mode-${m.k}`}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-[10px] uppercase tracking-widest transition ${
                  active ? "bg-[#F4B400] text-black font-bold" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}>
                <I className="w-3 h-3" /> {m.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main area */}
      <div className="relative z-10 p-8">
        {effectiveMode === "single" && <ModeSingleCourt court={court} matches={liveMatches} />}
        {effectiveMode === "all" && <ModeAllCourts matches={liveMatches} />}
        {effectiveMode === "standings" && <ModeStandings standings={standings} />}
        {effectiveMode === "bracket" && <ModeBracket qf={qf} />}
        {effectiveMode === "wildcard" && <ModeWildcard wildcards={wildcards} />}
        {effectiveMode === "sponsor" && <ModeSponsor />}
      </div>

      <div className="fixed bottom-3 right-4 text-[9px] uppercase tracking-widest text-zinc-600" data-testid="broadcast-heartbeat">
        LIVE · Refresh every 5s · Tick {tick}
      </div>
    </div>
  );
}

function ModeSingleCourt({ court, matches }) {
  const m = matches.find((x) => x.court === court) || matches[0];
  if (!m) return <div data-testid="broadcast-single-court"><EmptyState label={`No live match on ${court}`} /></div>;

  const ts = m.tennis_state;
  const isSetBased = m.format_type === "set_based" && ts;
  // Compute the current game display exactly like the backend engine.
  const ladder = ["0", "15", "30", "40"];
  let gameA = "0", gameB = "0", gameLabel = "";
  if (isSetBased) {
    if (ts.in_tiebreak) {
      gameA = String(ts.tiebreak_a || 0); gameB = String(ts.tiebreak_b || 0); gameLabel = "TIEBREAK";
    } else if (ts.deuce) {
      if (ts.advantage === "a") { gameA = "AD"; gameB = "40"; gameLabel = "ADVANTAGE A"; }
      else if (ts.advantage === "b") { gameA = "40"; gameB = "AD"; gameLabel = "ADVANTAGE B"; }
      else { gameA = "40"; gameB = "40"; gameLabel = "DEUCE"; }
    } else {
      gameA = ladder[Math.min(3, ts.game_a || 0)];
      gameB = ladder[Math.min(3, ts.game_b || 0)];
    }
  }
  const setScores = (m.set_scores || []).filter((s) => !s.in_progress);
  const totalGamesA = m.total_games_won_a ?? m.score_a ?? 0;
  const totalGamesB = m.total_games_won_b ?? m.score_b ?? 0;

  return (
    <div className="text-center pt-8" data-testid="broadcast-single-court">
      <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-4">{m.court} · {m.stage || ""}</div>

      {isSetBased ? (
        <>
          {gameLabel && (
            <div className={`inline-block px-4 py-1 rounded-full mb-4 text-xs font-bold uppercase tracking-widest ${
              gameLabel === "TIEBREAK" ? "bg-[#F4B400] text-black" :
              gameLabel === "DEUCE" ? "bg-[#e32424] text-white" :
              "bg-[#00A86B] text-black"
            }`} data-testid="broadcast-tennis-status">{gameLabel}</div>
          )}
          <div className="grid grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            {/* Team A */}
            <div className="text-right">
              <div className="text-3xl font-bold uppercase tracking-widest truncate">{m.team_a_name || m.team_a?.name || "—"}</div>
              <div className="font-anton text-[220px] leading-none tabular-nums text-[#F4B400]" data-testid="broadcast-game-a">{gameA}</div>
              <div className="flex items-center justify-end gap-2 mt-2">
                {setScores.map((s, i) => (
                  <div key={i} className="text-2xl font-anton tabular-nums bg-black/40 border border-white/10 rounded px-3 py-1">{s.a}</div>
                ))}
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2">Total games <b className="text-white">{totalGamesA}</b></div>
            </div>
            {/* Team B */}
            <div className="text-left">
              <div className="text-3xl font-bold uppercase tracking-widest truncate">{m.team_b_name || m.team_b?.name || "—"}</div>
              <div className="font-anton text-[220px] leading-none tabular-nums text-[#F4B400]" data-testid="broadcast-game-b">{gameB}</div>
              <div className="flex items-center gap-2 mt-2">
                {setScores.map((s, i) => (
                  <div key={i} className="text-2xl font-anton tabular-nums bg-black/40 border border-white/10 rounded px-3 py-1">{s.b}</div>
                ))}
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2">Total games <b className="text-white">{totalGamesB}</b></div>
            </div>
          </div>
        </>
      ) : (
        <>
          <ScoreRow name={m.team_a_name || m.team_a?.name || "—"} score={totalGamesA} />
          <div className="text-2xl uppercase tracking-widest text-zinc-500 my-4">vs</div>
          <ScoreRow name={m.team_b_name || m.team_b?.name || "—"} score={totalGamesB} />
        </>
      )}
    </div>
  );
}
function ModeAllCourts({ matches }) {
  if (matches.length === 0) return <div data-testid="broadcast-all-courts"><EmptyState label="No live matches right now" /></div>;
  const cols = matches.length === 1 ? "grid-cols-1" : matches.length <= 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2 md:grid-cols-3";
  return (
    <div className={`grid ${cols} gap-6`} data-testid="broadcast-all-courts">
      {matches.map((m) => {
        const td = m.tennis_display;
        const totalA = m.total_games_won_a ?? m.score_a ?? 0;
        const totalB = m.total_games_won_b ?? m.score_b ?? 0;
        return (
          <div key={m.id} className="p-6 rounded-2xl border border-white/10 bg-white/5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-[#F4B400]">{m.court}</div>
              {td?.label && (
                <div className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  td.label === "TIEBREAK" ? "bg-[#F4B400] text-black" :
                  td.label === "DEUCE" ? "bg-[#e32424] text-white" :
                  "bg-[#00A86B] text-black"
                }`}>{td.label}</div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 items-center">
              {/* Team A */}
              <div>
                <div className="text-lg font-bold uppercase tracking-widest truncate">{m.team_a_name || m.team_a?.name || "—"}</div>
                {td ? (
                  <>
                    <div className="font-anton text-6xl tabular-nums text-[#F4B400] leading-none">{td.a}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Games <b className="text-white">{totalA}</b></div>
                  </>
                ) : (
                  <div className="font-anton text-6xl tabular-nums text-[#F4B400]">{totalA}</div>
                )}
              </div>
              {/* Team B */}
              <div>
                <div className="text-lg font-bold uppercase tracking-widest truncate">{m.team_b_name || m.team_b?.name || "—"}</div>
                {td ? (
                  <>
                    <div className="font-anton text-6xl tabular-nums text-[#F4B400] leading-none">{td.b}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Games <b className="text-white">{totalB}</b></div>
                  </>
                ) : (
                  <div className="font-anton text-6xl tabular-nums text-[#F4B400]">{totalB}</div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
function ModeStandings({ standings }) {
  return (
    <div className="max-w-4xl mx-auto" data-testid="broadcast-standings">
      <h2 className="font-anton text-5xl uppercase tracking-tight mb-6 text-[#F4B400]">League Standings</h2>
      <table className="w-full text-lg">
        <thead className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
          <tr>
            <th className="text-left py-3 w-16">#</th>
            <th className="text-left py-3">Team</th>
            <th className="text-center py-3">W-L</th>
            <th className="text-right py-3">GD</th>
            <th className="text-right py-3 min-w-[120px]">Tie-break</th>
          </tr>
        </thead>
        <tbody>
          {standings.slice(0, 12).map((r, i) => (
            <tr key={r.team_id || i} className="border-b border-white/5">
              <td className="py-3 font-anton text-2xl">{i + 1}</td>
              <td className="py-3 font-bold">{r.team_name}</td>
              <td className="py-3 text-center tabular-nums">{r.won}-{r.lost}</td>
              <td className={`py-3 text-right tabular-nums font-bold ${r.game_diff > 0 ? "text-emerald-400" : r.game_diff < 0 ? "text-red-400" : ""}`}>
                {r.game_diff > 0 ? "+" : ""}{r.game_diff}
              </td>
              <td className="py-3 text-right text-[10px] uppercase tracking-widest text-zinc-500">{r.tie_breaker_used || "GD"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function ModeBracket({ qf }) {
  if (!qf || !qf.ready) return <div data-testid="broadcast-bracket"><EmptyState label="Bracket not published yet" /></div>;
  return (
    <div className="max-w-6xl mx-auto" data-testid="broadcast-bracket">
      <h2 className="font-anton text-5xl uppercase tracking-tight mb-8 text-center text-[#F4B400]">Playoff Bracket</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(qf.matchups || []).map((m) => (
          <div key={m.label} className="p-5 rounded-2xl border border-white/10 bg-white/5">
            <div className="text-[10px] uppercase tracking-widest text-[#F4B400] font-bold mb-3">{m.label}</div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold">#{m.seed_a} · {m.team_a?.team_name}</span>
            </div>
            <div className="text-sm text-zinc-500 uppercase tracking-widest my-1">vs</div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">#{m.seed_b} · {m.team_b?.team_name}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function ModeWildcard({ wildcards }) {
  return (
    <div className="max-w-4xl mx-auto" data-testid="broadcast-wildcard">
      <h2 className="font-anton text-5xl uppercase tracking-tight mb-6 text-[#F4B400]">Wildcard Race</h2>
      <div className="space-y-3">
        {wildcards.map((c) => (
          <div key={c.team_id} className={`p-4 rounded-lg border flex items-center gap-4 ${c.picked ? "border-[#F4B400]/40 bg-[#F4B400]/10" : "border-white/10 bg-white/5"}`}>
            <div className="font-anton text-3xl w-12 text-center">{c.rank}</div>
            <div className="flex-1">
              <div className="text-xl font-bold">{c.team_name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Grp {c.group} · Pos {c.position_in_group}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums">AGD {c.avg_game_diff.toFixed(2)}</div>
              {c.picked && <div className="text-[10px] uppercase tracking-widest text-[#F4B400] font-bold">{c.picked}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function ModeSponsor() {
  return (
    <div className="text-center pt-24" data-testid="broadcast-sponsor">
      <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-6">Proudly Presented By</div>
      <h1 className="font-anton text-7xl md:text-9xl uppercase tracking-tight text-[#F4B400]">Mara Padel</h1>
      <div className="text-2xl text-zinc-400 mt-4 uppercase tracking-widest">Season 03 · Nairobi</div>
    </div>
  );
}
function EmptyState({ label }) {
  return <div className="text-center py-32 text-2xl text-zinc-500 uppercase tracking-widest">{label}</div>;
}
function ScoreRow({ name, score }) {
  return (
    <div className="flex items-center justify-between max-w-4xl mx-auto px-8">
      <div className="text-4xl md:text-6xl font-bold truncate">{name}</div>
      <div className="font-anton text-9xl md:text-[220px] leading-none tabular-nums text-[#F4B400]">{score}</div>
    </div>
  );
}
